import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./guest-site.css";

export const WHATSAPP_URL = "https://wa.me/2348069804062";
export const COMMUNITY_URL =
  "https://chat.whatsapp.com/CNoXc9l7u9sJynDvppxDhg?mode=gi_t";
export const PEBBLE_URL = `${WHATSAPP_URL}?text=${encodeURIComponent("Hello Zaviramed, I would like to learn about Pebble.")}`;
export const asset = (name) => `/design/${name}`;

export function DesignIcon({ name, className = "" }) {
  return (
    <img
      className={`zv-icon ${className}`}
      src={asset(name)}
      alt=""
      aria-hidden="true"
    />
  );
}

export function WhatsAppButton({
  children = "Chat with a doctor",
  href = WHATSAPP_URL,
  dark = false,
  className = "",
}) {
  return (
    <a
      className={`zv-button ${dark ? "zv-button-dark" : ""} ${className}`}
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      <DesignIcon
        name={dark ? "pebble-imgVector3.svg" : "home-imgVector2.svg"}
      />
      {children}
    </a>
  );
}

export function CommunityButton({
  dark = false,
  children = "Join Community",
  className = "",
}) {
  return (
    <a
      className={`zv-button zv-community ${dark ? "zv-community-dark" : ""} ${className}`}
      href={COMMUNITY_URL}
      target="_blank"
      rel="noreferrer"
    >
      <DesignIcon name={dark ? "home-imgVector35.svg" : "home-imgVector.svg"} />
      {children}
    </a>
  );
}

export function Logo() {
  return (
    <Link className="zv-logo" to="/" aria-label="Zaviramed home">
      <img
        src={asset("home-imgBackgroundEraser202610171028362391.png")}
        alt=""
        width="55"
        height="55"
      />
      <span>Zaviramed</span>
    </Link>
  );
}

const nav = [
  ["About", "/#about"],
  ["Service", "/#services"],
  ["Reviews", "/#reviews"],
  ["Article", "/articles"],
  ["Pebble", "/pebble"],
  ["Kits", "/home-tests"],
  ["Contact Us", "/#contact"],
];

export function Header({ heroAligned = true }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return undefined;
    }
    const timer = window.setTimeout(
      () =>
        document
          .getElementById(location.hash.slice(1))
          ?.scrollIntoView({ behavior: "smooth" }),
      40,
    );
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);
  return (
    <header className={`zv-header${heroAligned ? " zv-header--hero-aligned" : ""}`}>
      <a className="zv-skip" href="#main-content">
        Skip to content
      </a>
      <div
        className="zv-container zv-header-inner"
        style={heroAligned ? {
          width: "min(1440px, calc(100% - 72px))",
          maxWidth: "none",
          marginInline: "auto",
          paddingLeft: 0,
          paddingRight: 0,
          justifyContent: "space-between",
          gap: "clamp(14px, 2vw, 38px)",
        } : undefined}
      >
        <Logo />
        <button
          type="button"
          className="zv-menu-toggle"
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open}
          aria-controls="guest-navigation"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? "Close" : "Menu"}
        </button>
        <div
          id="guest-navigation"
          className={`zv-navigation ${open ? "is-open" : ""}`}
        >
          <nav aria-label="Main navigation">
            {nav.map(([label, to]) => (
              <Link
                key={label}
                to={to}
                aria-current={location.pathname === to ? "page" : undefined}
                onClick={() => setOpen(false)}
              >
                {label}
              </Link>
            ))}
          </nav>
          <div className="zv-header-actions" onClick={() => setOpen(false)}>
            <CommunityButton />
            <WhatsAppButton>Start Chat</WhatsAppButton>
          </div>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="zv-footer" id="contact">
      <div className="zv-container">
        <div className="zv-footer-grid">
          <div>
            <Logo />
            <p className="zv-footer-caption">
              Doctor-led healthcare access starting from WhatsApp. Nigeria and
              beyond.
            </p>
          </div>
          <div className="zv-footer-columns">
            <div>
              <h2>PLATFORM</h2>
              <Link to="/#about">About</Link>
              <Link to="/#services">Services</Link>
              <Link to="/#reviews">Review</Link>
              <Link to="/articles">Articles</Link>
              <Link to="/home-tests">Product</Link>
              <a href="mailto:info@zaviramed.com.ng">Contact us</a>
            </div>
            <div>
              <h2>LEGAL</h2>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
            </div>
            <div>
              <h2>CONNECT</h2>
              <a
                href="https://instagram.com/zaviramed"
                target="_blank"
                rel="noreferrer"
              >
                Instagram
              </a>
              <a
                href="https://www.linkedin.com/company/zaviramed"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn
              </a>
              <a
                href="https://www.tiktok.com/@zaviramed"
                target="_blank"
                rel="noreferrer"
              >
                Tik Tok
              </a>
              <a href={COMMUNITY_URL} target="_blank" rel="noreferrer">
                Community
              </a>
            </div>
          </div>
        </div>
        <p className="zv-copyright">©2026 Zaviramed. All rights reserved.</p>
      </div>
    </footer>
  );
}

export function DoctorBanner() {
  return (
    <section className="zv-doctor-banner">
      <div className="zv-container zv-doctor-banner-inner">
        <div className="zv-doctor-banner-copy">
          <h2>Ready to speak with a doctor?</h2>
          <p>
            Send a WhatsApp message to Zaviramed.Tell us what is wrong and a
            doctor-led flow will guide your next step.
          </p>
          <div className="zv-actions">
            <WhatsAppButton />
            <CommunityButton dark />
          </div>
        </div>
        <img
          className="zv-doctor-banner-art"
          src={asset("doctor-consultation.png")}
          alt=""
          width="393"
          height="460"
          loading="lazy"
        />
      </div>
    </section>
  );
}

const patientReviews = [
  { name: "Tunde A.", location: "Abuja", initials: "TA", text: "The WhatsApp flow made everything simple. The doctor asked the right questions and explained clearly." },
  { name: "Fatima Odiri", location: "Lagos", initials: "FO", text: "Signing up and seeing a doctor was pretty quick and easy. Would recommend it if you're trying to avoid visiting a doctor in person." },
  { name: "Blessing N.", location: "Port Harcourt", initials: "BN", text: "Fast, reliable, and very professional. I got clear medical guidance without leaving my house." },
];

export function PatientReviews({ pebble = false }) {
  const [active, setActive] = useState(1);
  const review = patientReviews[active];
  const move = (direction) => setActive((index) => (index + direction + patientReviews.length) % patientReviews.length);
  return (
    <section className="zv-reviews" id="reviews" aria-label="Patient testimonials" aria-roledescription="carousel">
      <div className="zv-container">
        <h2>Our patients love using {pebble ? "Pebble" : "Zaviramed"}</h2>
        <figure>
          <blockquote aria-live="polite" aria-atomic="true">
            <DesignIcon name="home-imgVector32.svg" className="zv-quote-open" />
            <p key={active} className="zv-review-animated-text">{review.text}</p>
            <DesignIcon name={pebble ? "pebble-imgVector7.svg" : "home-imgVector33.svg"} className="zv-quote-close" />
          </blockquote>
          <figcaption>
            <div className="zv-patient-avatars" role="group" aria-label="Choose a patient review" style={{ "--zv-review-ring-x": `${active * 70}px` }}>
              {patientReviews.map((item, index) => (
                <button key={item.name} type="button" aria-label={`Show review from ${item.name}`} aria-pressed={active === index} onClick={() => setActive(index)} className="zv-patient-selector">
                  <span aria-hidden="true">{item.initials}</span>
                </button>
              ))}
            </div>
            <span key={`name-${active}`} className="zv-patient-name zv-review-animated-detail">{review.name}</span>
            <span key={`location-${active}`} className="zv-patient-location zv-review-animated-detail"><img src={asset("location-pin.png")} alt="" width="12" height="14" />{review.location.toUpperCase()}</span>
            <span className="zv-patient-stars" aria-label="5 out of 5 stars">★★★★★</span>
            <div className="zv-review-controls">
              <button type="button" onClick={() => move(-1)} aria-label="Previous review"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 4-8 8 8 8" /></svg></button>
              <button type="button" onClick={() => move(1)} aria-label="Next review"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 4 8 8-8 8" /></svg></button>
            </div>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
